/**
 * Helpers to derive stage-entry timestamps from a candidate's
 * `stage_history` array.
 *
 * Backend appends one entry `{stage, at, by, by_name}` each time the candidate
 * moves to a new stage. This util finds the LAST time a candidate entered a
 * given stage — used to render "Entered Screening · Jul 7 · 11:45 PM" style
 * subtitles on stage-gate frames (ScreeningPanel, OfferHireOutcomeModal, ...).
 */

/**
 * @param {Array<{stage:string, at:string}>} history
 * @param {string} stage
 * @returns {string|null} ISO timestamp or null
 */
export function stageEnteredAt(history, stage) {
    if (!Array.isArray(history) || !history.length || !stage) return null;
    for (let i = history.length - 1; i >= 0; i--) {
        if (history[i]?.stage === stage) return history[i].at || null;
    }
    return null;
}

/**
 * Formats an ISO timestamp for display in a compact stage-gate subtitle.
 * Example: "Jul 7 · 11:45 PM"
 */
export function formatEnteredAt(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const datePart = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
        const timePart = d.toLocaleTimeString(undefined, {
            hour: "numeric", minute: "2-digit", hour12: true,
        });
        return `${datePart} · ${timePart}`;
    } catch {
        return "";
    }
}

/**
 * Convenience: returns a display string like "Entered Screening · Jul 7 · 11:45 PM"
 * given a stage_history array and a stage name. Empty string when no data.
 */
export function stageEnteredLabel(history, stage) {
    const at = stageEnteredAt(history, stage);
    if (!at) return "";
    return `Entered ${stage} · ${formatEnteredAt(at)}`;
}
